import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, MessageSquareText, Sparkles, Tag, Users as UsersIcon, Key, BarChart3, Clock, MapPin } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CategoriesAdmin } from "@/pages/AdminCategories";
import { ALL } from "./admin/shared/constants";
import { StatCard } from "./admin/shared/ui";
import { RecordingsTab } from "./admin/RecordingsTab";
import { FeedbackTab } from "./admin/FeedbackTab";
import { QuestionsAdmin } from "./admin/QuestionsAdmin";
import { SeasonsAdmin } from "./admin/SeasonsAdmin";
import { VocabularyAdmin } from "./admin/VocabularyAdmin";
import { UsersAdmin } from "./admin/UsersAdmin";
import { LocationsAdmin } from "./admin/LocationsAdmin";
import { ApiKeysAdmin } from "./admin/ApiKeysAdmin";
import { WeekmailBlock, ReminderStatusLine, NudgeStatusLine, AiStatusLine, FollowupFeedbackBlock, WeeklyLearningPanel, AnalyticsAdmin } from "./admin/AnalyticsAdmin";
import { LogboekAdmin } from "./admin/LogboekAdmin";

export default function AdminPage() {
  const [tab, setTab] = useState("recordings");

  // recordings state
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [userId, setUserId] = useState(ALL);
  const [category, setCategory] = useState(ALL);

  // B3.2 — search across ALL memos (admin-only scope). Debounced, min 2 chars.
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchTokenRef = useRef(0); // FIX#8 (B-1) — discard a stale in-flight response
  const searching = search.trim().length >= 2;

  useEffect(() => {
    const q = search.trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (q.length < 2) {
      searchTokenRef.current += 1;
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      const myToken = ++searchTokenRef.current;
      try {
        const params = { q, scope: "all", limit: 50 };
        if (category !== ALL) params.category = category;
        const { data } = await api.get("/search", { params });
        if (searchTokenRef.current !== myToken) return; // a newer search already superseded this one
        setSearchResults(data);
      } catch (e) {
        if (searchTokenRef.current !== myToken) return;
        setSearchResults([]);
        toast.error(formatApiError(e));
      } finally {
        if (searchTokenRef.current === myToken) setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search, category]);

  // Polish#2/#3 — tab badge counts (open proposals / waiting suggestions)
  const [proposalsCount, setProposalsCount] = useState(0);
  const [suggestionsCount, setSuggestionsCount] = useState(0);
  const refreshBadgeCounts = async () => {
    try {
      const [pr, sg] = await Promise.all([
        api.get("/admin/proposals"),
        api.get("/admin/question-suggestions"),
      ]);
      setProposalsCount(pr.data.length);
      setSuggestionsCount(sg.data.length);
    } catch { /* fail-soft */ }
  };
  useEffect(() => { refreshBadgeCounts(); /* eslint-disable-next-line */ }, [tab]);
  // Fix#5d — also refresh right after a mutation changes the underlying counts (approve/
  // reject a proposal, activate/delete/generate a suggestion), not just on tab switch.
  useEffect(() => {
    window.addEventListener("tw1:admin-badges-dirty", refreshBadgeCounts);
    return () => window.removeEventListener("tw1:admin-badges-dirty", refreshBadgeCounts);
    // eslint-disable-next-line
  }, []);

  // feedback state
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userId !== ALL) params.user_id = userId;
      if (category !== ALL) params.category = category;
      const [recRes, userRes, statRes] = await Promise.all([
        api.get("/admin/recordings", { params }),
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setItems(recRes.data);
      setUsers(userRes.data);
      setStats(statRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const { data } = await api.get("/admin/feedback");
      setFeedback(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingFeedback(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [userId, category]);

  useEffect(() => {
    if (tab === "feedback") loadFeedback();
  }, [tab]);

  const clearFilters = () => {
    setUserId(ALL);
    setCategory(ALL);
  };

  const filtersActive = userId !== ALL || category !== ALL;
  const nonAdminUsers = useMemo(() => users.filter((u) => u.role !== "admin"), [users]);

  const handleDelete = async (id) => {
    if (!window.confirm("Deze opname verwijderen?")) return;
    setDeletingId(id);
    try {
      await api.delete(`/recordings/${id}`);
      setItems((prev) => prev.filter((r) => r.id !== id));
      setSearchResults((prev) => (prev ? prev.filter((r) => r.id !== id) : prev)); // FIX#7 (B-1)
      toast.success("Opname verwijderd.");
    } catch (e) {
      if (e?.response?.status === 404) {
        // FIX#7 (B-1) — already gone (another admin/tab deleted it first): drop it locally.
        setItems((prev) => prev.filter((r) => r.id !== id));
        setSearchResults((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
        toast.message("Opname was al verwijderd.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm("Deze feedback definitief verwijderen?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      setFeedback((prev) => prev.filter((f) => f.id !== id));
      toast.success("Feedback verwijderd.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-14" data-testid="admin-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8 pb-6 border-b border-tw">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">
              Beheeromgeving
            </p>
            <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black text-white leading-none tracking-tight">
              Beheer<span className="text-tw-yellow">.</span>
            </h1>
            <p className="mt-4 text-sm text-tw-muted max-w-lg leading-relaxed">
              Stuur op deelname, inhoud en opvolging — zonder de menselijke context te verliezen.
            </p>
          </div>
          <span
            className="shrink-0 inline-flex items-center gap-1.5 border border-tw-yellow text-tw-yellow px-2 py-1 font-mono text-[9px] tracking-[0.3em] uppercase"
            data-testid="admin-badge"
          >
            Admin
          </span>
        </div>
      </div>

      {/* Stats bento — mapped to real data */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="admin-stats">
          <StatCard
            label="Actieve gebruikers"
            value={stats.total_users}
            sub={`${nonAdminUsers.length} teamleden`}
            accent="#FFD700"
          />
          <StatCard
            label="Memo's deze week"
            value={stats.memos_this_week ?? 0}
            sub={`${stats.total_recordings} totaal in de kluis`}
            accent="#32D74B"
          />
          <StatCard
            label="Wacht op opvolging"
            value={stats.total_feedback ?? 0}
            sub="feedbackitems"
            accent="#FF3B30"
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-tw-surface border border-tw rounded-none p-0 h-auto w-full flex flex-wrap justify-start mb-6">
          <TabsTrigger
            value="recordings"
            data-testid="admin-tab-recordings"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <Inbox size={14} className="mr-2" />
            Opnames
            {proposalsCount > 0 && (
              <span data-testid="admin-tab-recordings-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-tw-red text-white text-[9px] font-bold">
                {proposalsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="feedback"
            data-testid="admin-tab-feedback"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <MessageSquareText size={14} className="mr-2" />
            Feedback
          </TabsTrigger>
          <TabsTrigger
            value="vragen"
            data-testid="admin-tab-vragen"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <Sparkles size={14} className="mr-2" />
            Vragen
            {suggestionsCount > 0 && (
              <span data-testid="admin-tab-vragen-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-tw-red text-white text-[9px] font-bold">
                {suggestionsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="categorieen"
            data-testid="admin-tab-categorieen"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <Tag size={14} className="mr-2" />
            Categorieën
          </TabsTrigger>
          <TabsTrigger
            value="locaties"
            data-testid="admin-tab-locaties"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <MapPin size={14} className="mr-2" />
            Locaties
          </TabsTrigger>
          <TabsTrigger
            value="gebruikers"
            data-testid="admin-tab-gebruikers"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <UsersIcon size={14} className="mr-2" />
            Gebruikers
          </TabsTrigger>
          <TabsTrigger
            value="koppelingen"
            data-testid="admin-tab-koppelingen"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <Key size={14} className="mr-2" />
            Koppelingen
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            data-testid="admin-tab-analytics"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <BarChart3 size={14} className="mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger
            value="logboek"
            data-testid="admin-tab-logboek"
            className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black data-[state=active]:shadow-none text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3"
          >
            <Clock size={14} className="mr-2" />
            Logboek
          </TabsTrigger>
          <TabsTrigger value="seizoenen" data-testid="admin-tab-seizoenen" className="rounded-none data-[state=active]:bg-tw-yellow data-[state=active]:text-black text-tw-muted hover:text-white uppercase tracking-[0.2em] text-xs font-bold px-5 py-3">Seizoenen</TabsTrigger>
        </TabsList>

        {/* --- Recordings tab --- */}
        <TabsContent value="recordings" className="mt-0">
          <RecordingsTab
            search={search}
            setSearch={setSearch}
            searching={searching}
            category={category}
            setCategory={setCategory}
            searchLoading={searchLoading}
            searchResults={searchResults}
            handleDelete={handleDelete}
            deletingId={deletingId}
            filtersActive={filtersActive}
            clearFilters={clearFilters}
            users={users}
            userId={userId}
            setUserId={setUserId}
            loading={loading}
            items={items}
          />
        </TabsContent>

        {/* --- Feedback tab --- */}
        <TabsContent value="feedback" className="mt-0">
          <FeedbackTab
            feedback={feedback}
            loadingFeedback={loadingFeedback}
            handleDeleteFeedback={handleDeleteFeedback}
          />
        </TabsContent>

        {/* --- Vragen tab --- */}
        <TabsContent value="seizoenen" className="mt-0"><SeasonsAdmin /></TabsContent>
        <TabsContent value="vragen" className="mt-0">
          <QuestionsAdmin />
          <div className="h-8" />
          <VocabularyAdmin />
        </TabsContent>

        {/* --- Categorieën tab (A1) --- */}
        <TabsContent value="categorieen" className="mt-0">
          <CategoriesAdmin />
        </TabsContent>

        {/* --- Locaties tab (Round I) --- */}
        <TabsContent value="locaties" className="mt-0">
          <LocationsAdmin />
        </TabsContent>

        {/* --- Gebruikers tab --- */}
        <TabsContent value="gebruikers" className="mt-0">
          <UsersAdmin />
        </TabsContent>

        {/* --- Koppelingen tab --- */}
        <TabsContent value="koppelingen" className="mt-0">
          <ApiKeysAdmin />
        </TabsContent>

        {/* --- Analytics tab --- */}
        <TabsContent value="analytics" className="mt-0">
          <WeekmailBlock />
          <ReminderStatusLine />
          <NudgeStatusLine />
          <AiStatusLine />
          <FollowupFeedbackBlock />
          <WeeklyLearningPanel />
          <AnalyticsAdmin />
        </TabsContent>

        {/* --- Logboek tab --- */}
        <TabsContent value="logboek" className="mt-0">
          <LogboekAdmin />
        </TabsContent>
      </Tabs>
    </div>
  );
}
